import { Button } from '@affine/component/ui/button';
import { useI18n } from '@affine/i18n';
import {
  type AutomationBlockModel,
  DEFAULT_CLEANUP_TIMEZONE,
  formatCleanupTimestamp,
  getCleanupSummary,
} from '@blocksuite/affine/blocks/streamlain';
import type { FilterGroup } from '@blocksuite/affine/data-view';
import {
  CRON_PRESET_VALUES,
  type CronPreset,
  isCronPreset,
} from '@streamlain/automation-safety-contract';
import {
  validateCleanupPolicy,
  validateCleanupTimezone,
} from '@streamlain/database-identity';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useRef, useState } from 'react';

import { DocsService } from '../../doc';
import { GuardService } from '../../permissions';
import { useAutomationBlockModel } from '../hooks/use-automation-block-model';
import { useBlockModelConfig } from '../hooks/use-block-model-config';
import { AutomationService } from '../services/automation';
import { AutomationAutoSaveService } from '../services/automation-auto-save';
import {
  AutomationConfigApiService,
  type CleanupPreviewResponse,
} from '../services/automation-config-api';
import { AutomationDiscoveryService } from '../services/automation-discovery';
import {
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../services/database-discovery';
import * as styles from './cleanup-panel.css';
import { NativeFilterPanel } from './components/native-filter-panel';
import { TableSelector } from './components/table-selector';

/**
 * Label of every schedule preset of the automation safety contract; the
 * contract's CRON_PRESET_VALUES is the one list, `satisfies` keeps the labels
 * complete when a preset is added there.
 */
const presetLabelKeys = {
  nightly: 'com.streamlain.streamlain.cleanup.nightly',
  every_1_min: 'com.streamlain.streamlain.process.schedule.every-1-min',
  every_5_min: 'com.streamlain.streamlain.process.schedule.every-5-min',
  every_15_min: 'com.streamlain.streamlain.process.schedule.every-15-min',
  every_30_min: 'com.streamlain.streamlain.process.schedule.every-30-min',
  hourly: 'com.streamlain.streamlain.cleanup.preset-hourly',
  daily_9am: 'com.streamlain.streamlain.cleanup.preset-daily',
  daily_6pm: 'com.streamlain.streamlain.process.schedule.daily-6pm',
  weekly_monday_9am: 'com.streamlain.streamlain.process.schedule.weekly-monday',
  monthly_first_9am: 'com.streamlain.streamlain.process.schedule.monthly',
} as const satisfies Record<CronPreset, string>;

function ageFilter(columnId: string, days: number): FilterGroup {
  return {
    type: 'group',
    op: 'and',
    conditions: [
      {
        type: 'filter',
        left: { type: 'ref', name: columnId },
        function: 'olderThanDays',
        args: [{ type: 'literal', value: days }],
      },
    ],
  };
}

/** Saved policy preview precedes explicit schedule activation. */
export function CleanupPanel({ blockId }: { blockId: string }) {
  const t = useI18n();
  const automation = useService(AutomationService);
  const api = useService(AutomationConfigApiService);
  const autoSave = useService(AutomationAutoSaveService);
  const discovery = useService(AutomationDiscoveryService);
  const databasesService = useService(DatabaseDiscoveryService);
  const docs = useService(DocsService);
  const guard = useService(GuardService);
  const info$ = useMemo(
    () => discovery.watchAutomation(blockId),
    [discovery, blockId]
  );
  const info = useLiveData(info$);
  const rawModel = useAutomationBlockModel(blockId, info?.docId, docs);
  const automationModel =
    rawModel?.flavour === 'affine:automation'
      ? (rawModel as AutomationBlockModel)
      : null;
  const model =
    automationModel?.props.executionKind === 'cleanup' ? automationModel : null;
  const config = useBlockModelConfig(model);
  const databases = useLiveData(databasesService.databases$);
  const selected = databases.find(
    db =>
      db.blockId === config?.sourceTableId && db.docId === config?.sourceDocId
  );
  const selectedDocId = selected?.docId;
  const hostRead$ = useMemo(
    () => (model ? guard.can$('Doc_Read', model.store.id) : null),
    [guard, model]
  );
  const hostWrite$ = useMemo(
    () => (model ? guard.can$('Doc_Update', model.store.id) : null),
    [guard, model]
  );
  const databaseRead$ = useMemo(
    () => (selectedDocId ? guard.can$('Doc_Read', selectedDocId) : null),
    [guard, selectedDocId]
  );
  const databaseWrite$ = useMemo(
    () => (selectedDocId ? guard.can$('Doc_Update', selectedDocId) : null),
    [guard, selectedDocId]
  );
  const hostRead = useLiveData(hostRead$) === true;
  const hostWrite = useLiveData(hostWrite$) === true;
  const databaseRead = useLiveData(databaseRead$) === true;
  const databaseWrite = useLiveData(databaseWrite$) === true;
  const canEdit =
    hostRead && hostWrite && (!selected || (databaseRead && databaseWrite));
  const summary = model ? getCleanupSummary(model.props) : null;
  const [daysDraft, setDaysDraft] = useState(String(summary?.days ?? 30));
  const [error, setError] = useState<string | null>(null);
  const [invalidDraft, setInvalidDraft] = useState(false);
  const [, refreshRuntime] = useState(0);
  const [preview, setPreview] = useState<{
    signature: string;
    result: CleanupPreviewResponse;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const signature = JSON.stringify({
    blockId,
    databaseId: config?.sourceTableId,
    docId: config?.sourceDocId,
    filter: config?.filterGroup,
    preset: config?.triggerConfig?.cronPreset,
    timezone: config?.triggerConfig?.timezone,
  });
  const signatureRef = useRef(signature);
  signatureRef.current = signature;
  const previousSignature = useRef<string | null>(null);
  const pausePending = useRef<Promise<void>>(Promise.resolve());
  useEffect(() => {
    const changed =
      previousSignature.current !== null &&
      previousSignature.current !== signature;
    previousSignature.current = signature;
    if (changed) setPreview(null);
    if (!changed || !model || !hostWrite) return;
    autoSave.cancelPendingSave(blockId);
    const pause = async () => {
      const saved = await api.getConfig(
        blockId,
        model.store.workspace.id,
        model.store.id
      );
      if (!saved) return;
      const schedule = await api.saveSchedule(
        blockId,
        model.store.workspace.id,
        model.store.id,
        {
          triggerType: 'scheduled',
          cronPreset: config?.triggerConfig?.cronPreset ?? 'nightly',
          timezone: config?.triggerConfig?.timezone ?? DEFAULT_CLEANUP_TIMEZONE,
          enabled: false,
        }
      );
      if (!schedule)
        throw new Error(t['com.streamlain.streamlain.cleanup.save-failed']());
      window.__automationRuntimeBridge?.setSchedule(blockId, schedule);
    };
    pausePending.current = pausePending.current.then(pause, pause);
    void pausePending.current.catch(error =>
      setError(
        error instanceof Error
          ? error.message
          : t['com.streamlain.streamlain.cleanup.save-failed']()
      )
    );
  }, [
    api,
    autoSave,
    blockId,
    config?.triggerConfig?.cronPreset,
    config?.triggerConfig?.timezone,
    hostWrite,
    model,
    signature,
    t,
  ]);
  useEffect(() => {
    setDaysDraft(String(summary?.days ?? 30));
    setError(null);
  }, [blockId, config?.sourceTableId, summary?.days]);
  useEffect(() => {
    if (!selected?.docId) return;
    const lease = databasesService.acquireDatabaseGroup(selected.docId, 'read');
    void lease.ready.catch(() =>
      setError(t['com.streamlain.streamlain.cleanup.unavailable']())
    );
    return lease.release;
  }, [databasesService, selected?.docId, t]);
  useEffect(() => {
    const update = (event: WindowEventMap['automation-runtime-updated']) => {
      if (event.detail.blockId === blockId) refreshRuntime(value => value + 1);
    };
    window.addEventListener('automation-runtime-updated', update);
    return () =>
      window.removeEventListener('automation-runtime-updated', update);
  }, [blockId]);

  useEffect(() => {
    if (model)
      window.__automationRuntimeBridge?.ensureRuntime(blockId, model.store.id);
  }, [blockId, model]);

  if (!model || !config || !summary || !hostRead) {
    return (
      <p className={styles.help} role="status">
        {t['com.streamlain.streamlain.cleanup.unavailable']()}
      </p>
    );
  }
  const modified = selected?.columns.find(
    column => column.type === 'updated-time'
  );
  const created = selected?.columns.find(
    column => column.type === 'created-time'
  );
  const runtime = window.__automationRuntimeBridge?.getRuntime(blockId);
  const lastResult = runtime?.history[0]?.cleanup;
  const nextRunAt = summary.enabled ? runtime?.schedule?.nextRunAt : null;
  const timezone = summary.timezone;
  const preset = summary.preset ?? 'nightly';
  const scheduleLabel = isCronPreset(preset)
    ? t[presetLabelKeys[preset]]()
    : t['com.streamlain.streamlain.cleanup.unavailable']();
  const ageBasis =
    summary.columnId === created?.id
      ? t['com.streamlain.streamlain.cleanup.since-created']()
      : t['com.streamlain.streamlain.cleanup.since-modified']();
  const policyValid = (() => {
    try {
      validateCleanupPolicy({ filter: model.props.filterGroup });
      return true;
    } catch {
      return false;
    }
  })();
  const changeRule = (filterGroup: FilterGroup) => {
    if (!canEdit) return;
    model.store.transact(() =>
      model.store.updateBlock(model, {
        filterGroup,
        triggerConfig: { ...model.props.triggerConfig, enabled: false },
      })
    );
  };
  const chooseDatabase = async (database: DatabaseInfo) => {
    if (!hostRead || !hostWrite) return;
    if (
      !(await guard.can('Doc_Read', database.docId)) ||
      !(await guard.can('Doc_Update', database.docId))
    )
      return;
    const modifiedColumn = database.columns.find(
      column => column.type === 'updated-time'
    );
    model.store.transact(() => {
      automation.bindCleanupDatabase(
        model,
        {
          workspaceId: model.store.workspace.id,
          docId: database.docId,
          databaseId: database.blockId,
        },
        'input',
        database.databaseTitle,
        database.columns
      );
    });
    setInvalidDraft(false);
    setDaysDraft(String(getCleanupSummary(model.props).days ?? 30));
    setError(
      modifiedColumn
        ? null
        : t['com.streamlain.streamlain.cleanup.date-missing']()
    );
  };
  const previewSaved = async () => {
    if (!canEdit || !selected || busy || invalidDraft || !policyValid) return;
    const requestSignature = signature;
    setBusy(true);
    setError(null);
    setPreview(null);
    autoSave.cancelPendingSave(blockId);
    try {
      await pausePending.current;
      model.store.updateBlock(model, {
        triggerConfig: { ...model.props.triggerConfig, enabled: false },
      });
      const saved = await api.saveConfig(blockId, model.store.workspace.id, {
        docId: model.store.id,
        executionKind: 'cleanup',
        sourceTableId: selected.blockId,
        sourceDocId: selected.docId,
        filterConfig: model.props.filterGroup,
      });
      if (!saved)
        throw new Error(t['com.streamlain.streamlain.cleanup.save-failed']());
      const schedule = await api.saveSchedule(
        blockId,
        model.store.workspace.id,
        model.store.id,
        {
          triggerType: 'scheduled',
          cronPreset: config.triggerConfig?.cronPreset ?? 'nightly',
          timezone,
          enabled: false,
        }
      );
      if (!schedule)
        throw new Error(t['com.streamlain.streamlain.cleanup.save-failed']());
      if (signatureRef.current !== requestSignature) return;
      const result = await api.previewCleanup(
        blockId,
        model.store.workspace.id,
        {
          docId: model.store.id,
          expectedConfigVersion: saved.updatedAt,
          expectedScheduleVersion: schedule.updatedAt,
        }
      );
      if (signatureRef.current === requestSignature)
        setPreview({ signature: requestSignature, result });
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : t['com.streamlain.streamlain.cleanup.save-failed']()
      );
    } finally {
      setBusy(false);
    }
  };
  const setEnabled = async (enabled: boolean) => {
    if (
      !canEdit ||
      busy ||
      (enabled && (!preview || preview.signature !== signature || error))
    )
      return;
    const requestSignature = signature;
    const acknowledgedPreview = preview?.result;
    setBusy(true);
    setError(null);
    autoSave.cancelPendingSave(blockId);
    try {
      await pausePending.current;
      const schedule = await api.saveSchedule(
        blockId,
        model.store.workspace.id,
        model.store.id,
        {
          triggerType: 'scheduled',
          cronPreset: config.triggerConfig?.cronPreset ?? 'nightly',
          timezone,
          enabled,
          ...(enabled && acknowledgedPreview
            ? {
                expectedConfigVersion: acknowledgedPreview.configVersion,
                expectedScheduleVersion: acknowledgedPreview.scheduleVersion,
              }
            : {}),
        }
      );
      if (!schedule || schedule.enabled !== enabled)
        throw new Error(t['com.streamlain.streamlain.cleanup.save-failed']());
      if (enabled && signatureRef.current !== requestSignature) {
        await api.saveSchedule(
          blockId,
          model.store.workspace.id,
          model.store.id,
          {
            triggerType: 'scheduled',
            cronPreset: config.triggerConfig?.cronPreset ?? 'nightly',
            timezone,
            enabled: false,
          }
        );
        throw new Error(t['com.streamlain.streamlain.cleanup.preview-stale']());
      }
      model.store.updateBlock(model, {
        triggerConfig: {
          ...model.props.triggerConfig,
          enabled: schedule.enabled,
        },
      });
      setPreview(null);
      window.__automationRuntimeBridge?.setSchedule(blockId, schedule);
      refreshRuntime(value => value + 1);
    } catch (error) {
      model.store.updateBlock(model, {
        triggerConfig: { ...model.props.triggerConfig, enabled: false },
      });
      setPreview(null);
      setError(
        error instanceof Error
          ? error.message
          : t['com.streamlain.streamlain.cleanup.save-failed']()
      );
    } finally {
      setBusy(false);
    }
  };

  const editDays = (value: string) => {
    setDaysDraft(value);
    const days = Number(value);
    if (!value.trim() || !Number.isSafeInteger(days) || days <= 0) {
      setInvalidDraft(true);
      setError(t['com.streamlain.streamlain.cleanup.invalid-days']());
      return;
    }
    const currentColumn = summary.columnId ?? modified?.id;
    if (!currentColumn) {
      setError(t['com.streamlain.streamlain.cleanup.date-missing']());
      return;
    }
    setInvalidDraft(false);
    setError(null);
    changeRule(ageFilter(currentColumn, days));
  };
  return (
    <div className={styles.panel} data-testid="cleanup-panel">
      <h2 className={styles.title}>
        {t['com.streamlain.streamlain.cleanup.title']()}
      </h2>
      <fieldset className={styles.step} disabled={!hostWrite}>
        <legend className={styles.label}>
          1. {t['com.streamlain.streamlain.cleanup.database']()}
        </legend>
        <TableSelector
          value={config.sourceTableId}
          currentDocId={model.store.id}
          requiredPermission="write"
          onChange={database =>
            void chooseDatabase(database).catch(() =>
              setError(t['com.streamlain.streamlain.cleanup.unavailable']())
            )
          }
          placeholder={t['com.streamlain.streamlain.cleanup.database-empty']()}
        />
      </fieldset>
      <fieldset className={styles.step} disabled={!canEdit}>
        <legend className={styles.label}>
          2. {t['com.streamlain.streamlain.cleanup.rule']()}
        </legend>
        <label htmlFor={`cleanup-days-${blockId}`}>
          {t['com.streamlain.streamlain.cleanup.days']()}
        </label>
        <input
          className={styles.input}
          id={`cleanup-days-${blockId}`}
          type="number"
          min="1"
          step="1"
          value={daysDraft}
          disabled={summary.days === null && summary.ruleCount > 0}
          aria-invalid={!!error}
          onChange={event => editDays(event.target.value)}
        />
        <p className={styles.help}>{ageBasis}</p>
        <p className={styles.help}>
          {summary.days !== null
            ? t['com.streamlain.streamlain.cleanup.rule-days']({
                days: String(summary.days),
              })
            : summary.ruleCount
              ? t['com.streamlain.streamlain.cleanup.rule-count']({
                  count: String(summary.ruleCount),
                })
              : t['com.streamlain.streamlain.cleanup.rule-empty']()}
        </p>
      </fieldset>
      <section
        className={styles.step}
        aria-label={t['com.streamlain.streamlain.cleanup.next-run']()}
      >
        <h3 className={styles.label}>3. {scheduleLabel}</h3>
        <p className={styles.help}>
          {preset === 'nightly'
            ? t['com.streamlain.streamlain.cleanup.distributed']()
            : scheduleLabel}{' '}
          · {timezone}
        </p>
        <p className={styles.help}>
          {t['com.streamlain.streamlain.cleanup.next-run']()}:{' '}
          {nextRunAt
            ? (formatCleanupTimestamp(nextRunAt, timezone) ?? '—')
            : '—'}
        </p>
        <Button
          onClick={() => {
            void previewSaved();
          }}
          disabled={
            !canEdit || !selected || busy || invalidDraft || !policyValid
          }
        >
          {t['com.streamlain.streamlain.cleanup.preview']()}
        </Button>
        <p className={styles.help} role="status">
          {busy
            ? t['com.streamlain.streamlain.cleanup.saving']()
            : preview
              ? t['com.streamlain.streamlain.cleanup.sample-count']({
                  count: String(preview.result.sampled),
                })
              : t['com.streamlain.streamlain.cleanup.preview-unavailable']()}
        </p>
        {preview && (
          <ul
            className={styles.help}
            aria-label={t['com.streamlain.streamlain.cleanup.sample']()}
          >
            {preview.result.sample.map(row => (
              <li key={row.id}>
                {Object.values(row.cells)
                  .map(cell => {
                    const column = selected?.columns.find(
                      column => column.id === cell.columnId
                    );
                    const value =
                      column &&
                      ['date', 'created-time', 'updated-time'].includes(
                        column.type
                      ) &&
                      typeof cell.value === 'number'
                        ? formatCleanupTimestamp(cell.value, timezone)
                        : typeof cell.value === 'string' ||
                            typeof cell.value === 'number'
                          ? String(cell.value)
                          : '—';
                    return `${column?.name ?? cell.columnId}: ${value ?? '—'}`;
                  })
                  .join(' · ')}
              </li>
            ))}
          </ul>
        )}

        {lastResult && (
          <p className={styles.help} role="status">
            {t['com.streamlain.streamlain.cleanup.result']({
              deleted: String(lastResult.deleted),
              changed: String(lastResult.skippedChanged),
              missing: String(lastResult.skippedMissing),
            })}
            {lastResult.status === 'partial'
              ? ` · ${t['com.streamlain.streamlain.cleanup.partial']()}`
              : ''}
          </p>
        )}
        <p className={styles.help}>
          {t['com.streamlain.streamlain.cleanup.delete-hint']()}
        </p>
        <Button
          onClick={() => {
            void setEnabled(!summary.enabled);
          }}
          disabled={
            !canEdit ||
            busy ||
            (!summary.enabled &&
              (!preview || preview.signature !== signature || !!error))
          }
        >
          {summary.enabled
            ? t['com.streamlain.streamlain.cleanup.pause']()
            : t['com.streamlain.streamlain.cleanup.arm']()}
        </Button>
        <p className={styles.help}>
          {t['com.streamlain.streamlain.cleanup.free']()}
        </p>
      </section>
      {error && (
        <p role="alert" className={styles.help}>
          {error}
        </p>
      )}
      <details>
        <summary className={styles.label}>
          {t['com.streamlain.streamlain.cleanup.advanced']()}
        </summary>
        <div className={styles.advanced}>
          <fieldset className={styles.step} disabled={!canEdit}>
            <label htmlFor={`cleanup-preset-${blockId}`}>
              {t['com.streamlain.streamlain.cleanup.preset']()}
            </label>
            <select
              className={styles.input}
              id={`cleanup-preset-${blockId}`}
              value={model.props.triggerConfig.cronPreset ?? 'nightly'}
              onChange={event => {
                if (!canEdit) return;
                const preset = event.target.value;
                if (!isCronPreset(preset)) return;
                model.store.updateBlock(model, {
                  triggerConfig: {
                    ...model.props.triggerConfig,
                    type: 'scheduled',
                    cronPreset: preset,
                    enabled: false,
                  },
                });
              }}
            >
              {CRON_PRESET_VALUES.map(preset => (
                <option key={preset} value={preset}>
                  {t[presetLabelKeys[preset]]()}
                </option>
              ))}
            </select>
            <label htmlFor={`cleanup-basis-${blockId}`}>
              {t['com.streamlain.streamlain.cleanup.basis']()}
            </label>
            <select
              className={styles.input}
              id={`cleanup-basis-${blockId}`}
              value={
                config.filterGroup.conditions[0]?.type === 'filter'
                  ? config.filterGroup.conditions[0].left.name
                  : ''
              }
              onChange={event =>
                changeRule(ageFilter(event.target.value, summary.days ?? 30))
              }
            >
              <option value="" disabled>
                {t['com.streamlain.streamlain.cleanup.rule-empty']()}
              </option>
              {modified && (
                <option value={modified.id}>
                  {t['com.streamlain.streamlain.cleanup.since-modified']()}
                </option>
              )}
              {created && (
                <option value={created.id}>
                  {t['com.streamlain.streamlain.cleanup.since-created']()}
                </option>
              )}
            </select>
            <label htmlFor={`cleanup-zone-${blockId}`}>
              {t['com.streamlain.streamlain.cleanup.timezone']()}
            </label>
            <input
              className={styles.input}
              id={`cleanup-zone-${blockId}`}
              defaultValue={timezone}
              onBlur={event => {
                if (!canEdit) return;
                try {
                  validateCleanupTimezone(event.target.value);
                  model.store.updateBlock(model, {
                    triggerConfig: {
                      ...model.props.triggerConfig,
                      timezone: event.target.value,
                      enabled: false,
                    },
                  });
                  setInvalidDraft(false);
                  setError(null);
                } catch {
                  setInvalidDraft(true);
                  setError(
                    t['com.streamlain.streamlain.cleanup.invalid-timezone']()
                  );
                }
              }}
            />
          </fieldset>
          {canEdit && (
            <NativeFilterPanel
              sourceTableId={config.sourceTableId}
              filterGroup={config.filterGroup}
              onChange={changeRule}
              noWrapper
            />
          )}
          <p className={styles.help}>
            {t['com.streamlain.streamlain.cleanup.last-run']()}:{' '}
            {summary.lastRunAt
              ? (formatCleanupTimestamp(summary.lastRunAt, timezone) ?? '—')
              : '—'}
          </p>
        </div>
      </details>
    </div>
  );
}
